import { useEffect, useMemo, useRef, useState } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { toPng } from 'html-to-image';
import { IconX } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

/** Drawn well above the page it will land on, so a stretched formula stays crisp. */
const RASTER_SCALE = 4;

/** The few anyone reaches for, as a starting point rather than a whole keyboard. */
const SNIPPETS = [
    { label: '¹⁄₂', tex: '\\frac{a}{b}' },
    { label: 'x²', tex: 'x^{2}' },
    { label: 'x₁', tex: 'x_{1}' },
    { label: '√', tex: '\\sqrt{x}' },
    { label: '∑', tex: '\\sum_{i=1}^{n}' },
    { label: '∫', tex: '\\int_{a}^{b}' },
    { label: 'π', tex: '\\pi' },
    { label: '≤', tex: '\\leq' },
    { label: '→', tex: '\\rightarrow' },
    { label: '∞', tex: '\\infty' },
];

/**
 * Equation editor: type TeX, watch it render, place it on the page.
 *
 * KaTeX draws synchronously into HTML and CSS, which is why this editor uses it
 * rather than MathJax — the same reason the question-paper tool does. What goes
 * onto the page is a PNG of that render: a PDF has to carry its own glyphs, and
 * the fonts this editor embeds are the document's, not KaTeX's, so a formula
 * left as text would export as a row of empty boxes.
 */
export default function EquationModal({ onClose, onApply }) {
    const { t } = useTranslation();
    const [tex, setTex] = useState('');
    const [busy, setBusy] = useState(false);
    const previewRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
        window.addEventListener('keydown', onKey, true);
        requestAnimationFrame(() => inputRef.current?.focus());
        return () => window.removeEventListener('keydown', onKey, true);
    }, [onClose]);

    /** The render, or the reason it will not render — both come from KaTeX. */
    const render = useMemo(() => {
        const source = tex.trim();
        if (!source) return { html: '', error: null };
        try {
            return {
                html: katex.renderToString(source, {
                    displayMode: true,
                    throwOnError: true,
                    // No MathML twin: html-to-image would paint the hidden copy
                    // on top of the visible maths.
                    output: 'html',
                }),
                error: null,
            };
        } catch (err) {
            return { html: '', error: err?.message || String(err) };
        }
    }, [tex]);

    const insert = async () => {
        const node = previewRef.current;
        if (!node || !render.html || busy) return;
        setBusy(true);
        try {
            const rect = node.getBoundingClientRect();
            const url = await toPng(node, {
                pixelRatio: RASTER_SCALE,
                backgroundColor: undefined,
                cacheBust: true,
            });
            onApply({
                src: url,
                width: Math.max(1, Math.round(rect.width * RASTER_SCALE)),
                height: Math.max(1, Math.round(rect.height * RASTER_SCALE)),
            });
            onClose();
        } finally {
            setBusy(false);
        }
    };

    const addSnippet = (snippet) => {
        setTex((prev) => (prev ? `${prev} ${snippet}` : snippet));
        inputRef.current?.focus();
    };

    return (
        <div
            className="fixed inset-0 z-[9200] flex items-center justify-center bg-[#1C1815]/45 p-4"
            role="presentation"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t('pdfEditor.equation.title')}
                onClick={(e) => e.stopPropagation()}
                className="flex w-full max-w-[520px] flex-col overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_20px_50px_rgba(28,24,21,0.25)] dark:border-zinc-700 dark:bg-zinc-900"
            >
                <div className="flex items-center justify-between border-b border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                    <h2 className="text-[14px] font-bold text-[#1C1815] dark:text-zinc-100">
                        {t('pdfEditor.equation.title')}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#A8A099] transition-colors hover:bg-[#F7F5F3] hover:text-[#3D3833] dark:hover:bg-zinc-800"
                    >
                        <IconX size={16} stroke={2} />
                    </button>
                </div>

                <div className="space-y-3 p-4">
                    <div className="flex flex-wrap gap-1">
                        {SNIPPETS.map((s) => (
                            <button
                                key={s.tex}
                                type="button"
                                onClick={() => addSnippet(s.tex)}
                                title={s.tex}
                                className="h-8 min-w-8 rounded-lg border border-[#ECE8E4] bg-white px-2 text-[14px] text-[#3D3833] transition-colors hover:border-amber-400 hover:text-amber-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>

                    <textarea
                        ref={inputRef}
                        rows={3}
                        value={tex}
                        onChange={(e) => setTex(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder={t('pdfEditor.equation.placeholder')}
                        spellCheck={false}
                        className="w-full resize-none rounded-lg border border-[#ECE8E4] bg-white px-3 py-2 font-mono text-[13px] text-[#1C1815] outline-none transition-colors placeholder:text-[#A8A099] focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                    />

                    <div className="rounded-lg border border-[#ECE8E4] bg-[#FAFAF9] p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
                        {render.error ? (
                            <p className="text-center text-[12px] leading-relaxed text-[#D33A2C]">
                                {render.error}
                            </p>
                        ) : render.html ? (
                            <div className="flex justify-center overflow-x-auto">
                                {/* The exact node that gets rasterised, so what is
                                    previewed is what lands on the page. */}
                                <div
                                    ref={previewRef}
                                    className="inline-block px-1 py-0.5 text-[#1C1815] dark:text-zinc-100"
                                    dangerouslySetInnerHTML={{ __html: render.html }}
                                />
                            </div>
                        ) : (
                            <p className="text-center text-[12px] text-[#A8A099] dark:text-zinc-500">
                                {t('pdfEditor.equation.empty')}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-700">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-10 rounded-xl border border-[#ECE8E4] bg-white px-4 text-[13.5px] font-semibold text-[#3D3833] transition-colors hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={insert}
                        disabled={!render.html || busy}
                        className="h-10 rounded-xl bg-primary px-5 text-[13.5px] font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-[#F7F5F3] disabled:text-[#A8A099] dark:disabled:bg-zinc-800 dark:disabled:text-zinc-600"
                    >
                        {t('pdfEditor.equation.insert')}
                    </button>
                </div>
            </div>
        </div>
    );
}
